import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminAudit } from '../../api/adminClient'
import { Download } from 'lucide-react'

export default function AdminAuditLog() {
  const { t } = useTranslation('portal')
  const [action, setAction] = useState('')
  const [entityType, setEntityType] = useState('')
  const [page, setPage] = useState(0)
  const [exporting, setExporting] = useState(false)
  const limit = 50

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await adminAudit.exportCsv({
        action: action || undefined,
        entity_type: entityType || undefined,
        limit: 10000,
      })
      const data = (res as { data: Blob }).data
      const url = URL.createObjectURL(new Blob([data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'audit_logs.csv'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit', action, entityType, page],
    queryFn: async () => {
      const res = await adminAudit.list({ action: action || undefined, entity_type: entityType || undefined, skip: page * limit, limit })
      return res.data
    },
  })

  const items = (data?.items ?? []) as {
    id: string
    user_id: string | null
    action: string
    entity_type: string | null
    entity_id: string | null
    changes: unknown
    ip_address: string | null
    timestamp: string
  }[]
  const total = data?.total ?? 0

  const entityTypeLabel = (et: string | null) => {
    if (!et) return et
    const map: Record<string, string> = {
      company: t('admin.audit.entityCompany'),
      payment: t('admin.audit.entityPayment'),
      payout: t('admin.audit.entityPayout'),
      user: t('admin.audit.entityUser'),
    }
    return map[et] ?? et
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-4">{t('admin.audit.title')}</h1>
      <div className="flex gap-4 mb-4 flex-wrap items-center">
        <input
          type="text"
          placeholder={t('admin.audit.filterByAction')}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 w-64 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">{t('admin.audit.allEntityTypes')}</option>
          <option value="company">{t('admin.audit.entityCompany')}</option>
          <option value="payment">{t('admin.audit.entityPayment')}</option>
          <option value="payout">{t('admin.audit.entityPayout')}</option>
          <option value="user">{t('admin.audit.entityUser')}</option>
        </select>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 rounded-lg bg-gray-100 border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? t('admin.audit.exporting') : t('admin.audit.exportCsv')}
        </button>
      </div>
      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 text-gray-500">{t('admin.common.loading')}</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500 text-sm bg-gray-50">
                <th className="p-3">{t('admin.audit.time')}</th>
                <th className="p-3">{t('admin.audit.user')}</th>
                <th className="p-3">{t('admin.audit.action')}</th>
                <th className="p-3">{t('admin.audit.entity')}</th>
                <th className="p-3">{t('admin.audit.ip')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((log) => (
                <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="p-3 text-gray-500 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="p-3 text-gray-600 font-mono text-sm">{log.user_id?.slice(0, 8) ?? '—'}…</td>
                  <td className="p-3 text-emerald-600">{log.action}</td>
                  <td className="p-3 text-gray-600 text-sm">
                    {entityTypeLabel(log.entity_type)}
                    {log.entity_id ? ` / ${String(log.entity_id).slice(0, 8)}…` : ''}
                  </td>
                  <td className="p-3 text-gray-500 text-sm">{log.ip_address ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {total > limit && (
          <div className="p-3 border-t border-gray-200 flex justify-between items-center bg-gray-50">
            <span className="text-gray-500 text-sm">{t('admin.common.total')}: {total}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
              >
                {t('admin.common.previous')}
              </button>
              <button
                type="button"
                disabled={(page + 1) * limit >= total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
              >
                {t('admin.common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
